import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAppDispatch } from '../../../../configuration/setup/store';
import { useGetContractsQuery } from '../../api/contracts/contractApiSlice';
import { useGetAllNonLegacyProductsQuery } from '../../api/product/productApiSlice';
import type { ApiAssetInfo } from '../../api/tags/tag.types';
import { useGetTagsQuery } from '../../api/tags/tagApiSlice';
import { fetchAssets } from '../../api/tags/tagCalls';
import { fetchDiscountsThunk } from '../../common/discount/discounts.thunk';
import { RenewDialogContainer } from '../../common/termedLevel/dialog/RenewDialog';
import { BookingOverviewProductListContainer } from './BookingOverviewProductList';
import {
    BookingOverviewEmptyState,
    BookingOverviewErrorState,
    BookingOverviewForbiddenState,
} from './BookingOverviewStates';
import { BookingOverviewStats } from './BookingOverviewStats';
import { BookingOverviewToolbar } from './BookingOverviewToolbar';
import type { BookingOverviewGrouping } from './bookingOverviewColumns';
import { filterBookingOverviewContracts } from './bookingOverviewFilters';
import { getBookingOverviewStatistics } from './bookingOverviewService';
import { useBookingOverviewColumns } from './useBookingOverviewColumns';
import { useBookingOverviewUrlState } from './useBookingOverviewUrlState';

export function BookingOverviewContainer() {
    const dispatch = useAppDispatch();
    const {
        data: contracts,
        error: contractsError,
        isError: hasContractsError,
        isFetching: areContractsFetching,
        refetch: refetchContracts,
    } = useGetContractsQuery(undefined);
    const {
        data: products,
        isError: hasProductsError,
        isFetching: areProductsFetching,
        refetch: refetchProducts,
    } = useGetAllNonLegacyProductsQuery();
    const {
        data: tags,
        isError: hasTagsError,
        refetch: refetchTags,
    } = useGetTagsQuery(undefined, { skip: !contracts?.length });
    const [assets, setAssets] = useState<ApiAssetInfo[]>();
    const [hasAssetError, setHasAssetError] = useState(false);
    const [reloadCount, setReloadCount] = useState(0);
    const { filters, setFilters, search, setSearch, clearFilters } = useBookingOverviewUrlState();
    const [grouping, setGrouping] = useState<BookingOverviewGrouping>('services');
    const [visibleColumns, setVisibleColumns] = useBookingOverviewColumns();

    const reload = () => {
        setAssets(undefined);
        setHasAssetError(false);
        setReloadCount(count => count + 1);
        refetchContracts();
        refetchProducts();
        if (contracts?.length) {
            refetchTags();
        }
    };

    useEffect(() => {
        dispatch(fetchDiscountsThunk());
    }, [dispatch, reloadCount]);

    useEffect(() => {
        let isMounted = true;
        fetchAssets()
            .then(assets => {
                if (isMounted) {
                    setAssets(assets);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setHasAssetError(true);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [reloadCount]);

    if (hasContractsError && !areContractsFetching) {
        if (
            contractsError &&
            'status' in contractsError &&
            (contractsError.status === 401 || contractsError.status === 403)
        ) {
            return <BookingOverviewForbiddenState />;
        }

        return <BookingOverviewErrorState onReload={reload} />;
    }

    if (areContractsFetching || !contracts) {
        return <Spinner text={<FormattedMessage id='intl-msg:marketplace.loading' />} isInverse={false} />;
    }

    if (contracts.length === 0) {
        return <BookingOverviewEmptyState />;
    }

    if (hasAssetError || hasProductsError || hasTagsError) {
        return <BookingOverviewErrorState onReload={reload} />;
    }

    if (!assets || !products || !tags || areProductsFetching) {
        return <Spinner text={<FormattedMessage id='intl-msg:marketplace.loading' />} isInverse={false} />;
    }

    const filteredContracts = filterBookingOverviewContracts(contracts, filters, search, assets);

    return (
        <div className='BookingOverviewPage'>
            <div className='BookingOverviewContent padding-x-30'>
                <BookingOverviewStats statistics={getBookingOverviewStatistics(contracts, assets)} />
                <BookingOverviewToolbar
                    contracts={contracts}
                    fleetGroups={tags}
                    filters={filters}
                    onChange={setFilters}
                    onClear={clearFilters}
                    grouping={grouping}
                    onGroupingChange={setGrouping}
                    search={search}
                    onSearchChange={setSearch}
                    visibleColumns={visibleColumns}
                    onVisibleColumnsChange={setVisibleColumns}
                />
                {filteredContracts.length ? (
                    <BookingOverviewProductListContainer
                        contracts={contracts}
                        filteredContracts={filteredContracts}
                        products={products}
                        assets={assets}
                        grouping={grouping}
                        visibleColumns={visibleColumns}
                    />
                ) : (
                    <EmptyState
                        headline={<FormattedMessage id='intl-msg:marketplace.bookings.overview.filters.noMatches' />}
                        message={<FormattedMessage id='intl-msg:marketplace.contracts.search.notFound.text' />}
                    />
                )}
                <RenewDialogContainer titleId='intl-msg:common-message.contracts.renewal' />
            </div>
        </div>
    );
}
